import React, { useState, useMemo, useCallback, memo } from 'react';
import { Flex, Button, Pagination } from 'antd';
import {
  FREE_ASSETS,
  CATEGORIES,
} from '@src/components/side-bar-menu/web2-asset-store/items/free-assets';
import { useBranding } from '@src/theme/branding/useBranding';
import { getAssetType } from '@src/utils/assetHelpers';
import { ASSETS_2022_CDN } from 'util/constants';
import { ASSET_TYPE } from '@src/components/scene/react-three-components/sceneAsset';
import ModelViewerModal from '@src/components/side-bar-menu/web2-asset-store/ModelViewer';
import styles from '../add-to-scene.module.css';

const PAGE_LIMIT = 20;

function paginate(array: any[], currentPage: number): any[] {
  return array.slice(currentPage * PAGE_LIMIT, (currentPage + 1) * PAGE_LIMIT);
}

export const FreeAssetsGridCards = ({
  freeAssets,
  onLoadAsset,
  onOpenPreview,
}) => {
  return (
    <div className={styles.gridContainer}>
      {freeAssets?.map((el, i) => {
        const assetType = el.modelType || el.fileType;
        const canPreview = assetType === ASSET_TYPE.GLTF.OTHER && el.assetURL;

        return (
          <div
            key={`${i}-${el.name}`}
            className={styles.libraryItem}
            onClick={() => onLoadAsset(el)}
          >
            <img
              src={
                el.previewImage ||
                el.thumbnail ||
                `${ASSETS_2022_CDN}/Studio/scene-assets-image.png`
              }
              alt={el.name || `Asset ${i}`}
              className={styles.libraryThumbnail}
              onClick={(e) => {
                e.stopPropagation();
                if (canPreview) {
                  onOpenPreview(el.assetURL);
                }
              }}
              style={{ cursor: canPreview ? 'pointer' : 'default' }}
            />

            {canPreview && (
              <div
                className={styles.previewIcon}
                onClick={(e) => {
                  e.stopPropagation();
                  onOpenPreview(el.assetURL);
                }}
                title='Preview'
              >
                👁
              </div>
            )}

            <div className={styles.libraryItemName}>
              {el.name || `Asset ${i}`}
            </div>

            <button
              className={styles.addToSceneButton}
              onClick={(e) => {
                e.stopPropagation();
                onLoadAsset(el);
              }}
            >
              Add to scene
            </button>
          </div>
        );
      })}
    </div>
  );
};

const FreeAssetsGridCardsMemo = memo(FreeAssetsGridCards);

export default function FreeAssetsGrid({ onAddToScene }) {
  const { isZepeto } = useBranding();
  const [currentPage, setCurrentPage] = useState<number>(0);
  const [filterType, setFilterType] = useState<string>('all');
  const [filterCategory, setFilterCategory] = useState<string>('all');
  const [active, setActive] = useState<number>(0);
  const [currentLength, setCurrentLength] = useState<number>(0);
  const [activeCategory, setActiveCategory] = useState<number>(0);
  const [previewOpen, setPreviewOpen] = useState<boolean>(false);
  const [modelUrl, setModelUrl] = useState<string>('');

  const freeAssets = useMemo(() => {
    const assets =
      filterType === 'all'
        ? FREE_ASSETS.single_asset
        : FREE_ASSETS[filterType] || [];

    if (filterCategory === 'all') {
      setCurrentLength(assets.length);
      return paginate(assets, currentPage);
    }
    const filteredAssets = assets.filter(
      (el) => el.categoryName === filterCategory
    );
    setCurrentLength(filteredAssets.length);
    return paginate(filteredAssets, currentPage);
  }, [filterType, currentPage, filterCategory]);

  const onLoadAsset = useCallback(
    (asset) => {
      if (asset.assetURL.includes('AssetPack.gltf')) {
        return;
      }

      if (asset.modelType === 'SCENE') {
        return;
      }

      const fileType = asset.modelType || asset.fileType;
      const assetType = getAssetType(fileType);

      onAddToScene({
        type: assetType,
        name: asset.name,
        url: asset.assetURL,
        previewImage: asset.previewImage,
      });
    },
    [onAddToScene]
  );

  const onOpenPreview = useCallback((url) => {
    setPreviewOpen(true);
    setModelUrl(url);
  }, []);

  const resetFilters = useCallback(() => {
    setFilterCategory('all');
    setActiveCategory(0);
    setCurrentPage(0);
  }, []);

  const handleCategoryClick = useCallback((categoryName, index) => {
    setFilterCategory(categoryName);
    setActiveCategory(index + 1);
    setCurrentPage(0);
  }, []);

  return (
    <div className={styles.contentWrapper}>
      <div className={styles.topSection}>
        <h3 className={styles.sectionTitle}>Free Assets</h3>
        <Flex
          align='center'
          justify='space-between'
          style={{ marginBottom: 20 }}
        >
          <Flex align='flex-start' gap={5}>
            <Button
              onClick={resetFilters}
              className={`${activeCategory === 0 && styles.categoryActive} ${
                styles.categoryButton
              }`}
            >
              All
            </Button>
            {filterType !== 'zepeto' &&
              CATEGORIES.map((categoryName, index) => (
                <Button
                  key={index}
                  onClick={() => handleCategoryClick(categoryName, index)}
                  className={`${
                    activeCategory === index + 1 && styles.categoryActive
                  } ${styles.categoryButton}`}
                >
                  {categoryName}
                </Button>
              ))}
          </Flex>
        </Flex>
      </div>

      {freeAssets.length ? (
        <FreeAssetsGridCardsMemo
          freeAssets={freeAssets}
          onLoadAsset={onLoadAsset}
          onOpenPreview={onOpenPreview}
        />
      ) : (
        <span className={styles.emptyContent}>No free assets available</span>
      )}

      {currentLength > PAGE_LIMIT && (
        <div style={{ marginTop: '1rem', textAlign: 'center' }}>
          <Pagination
            current={currentPage + 1}
            total={currentLength}
            pageSize={PAGE_LIMIT}
            onChange={(page) => setCurrentPage(page - 1)}
            showSizeChanger={false}
            showQuickJumper={false}
            showTotal={(total, range) =>
              `${range[0]}-${range[1]} of ${total} items`
            }
          />
        </div>
      )}

      <ModelViewerModal
        open={previewOpen}
        setOpen={setPreviewOpen}
        modelUrl={modelUrl}
      />
    </div>
  );
}
